import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Backdrop,
  CircularProgress,
  Tooltip,
  Grid,
  Alert,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import GroupsIcon from '@mui/icons-material/Groups';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useMediaQuery } from 'react-responsive';

import { useUserAuth } from '../../context/UserAuthContext';
import Header from '../../components/Header/Header';
import Logo from '../../components/Logo/Logo';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return '0';
  return new Intl.NumberFormat('en-US').format(value);
};

const CouponDashboard = () => {
  const navigate = useNavigate();
  const { user: firebaseUser, loading: authenticating } = useUserAuth();
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });

  // Data state
  const [data, setData] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [selectedStore, setSelectedStore] = useState('all');
  const [channel, setChannel] = useState('core');

  // Fetch stores on mount
  useEffect(() => {
    if (firebaseUser) {
      fetchStores();
    }
  }, [firebaseUser]);

  // Fetch data when filters change
  useEffect(() => {
    if (firebaseUser && startDate && endDate) {
      fetchData();
    }
  }, [firebaseUser, startDate, endDate, selectedStore, channel]);

  const fetchStores = async () => {
    try {
      const token = await firebaseUser.getIdToken(true);
      const response = await axios.get('/hd-api/olo-core/stores', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStores(response.data.stores || []);
    } catch (err) {
      console.error('Error fetching stores:', err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await firebaseUser.getIdToken(true);

      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        channel,
      });

      if (selectedStore !== 'all') {
        params.append('storeNumber', selectedStore);
      }

      const response = await axios.get(`/hd-api/olo-core/coupon-analytics?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setData(response.data);
    } catch (err) {
      console.error('Error fetching coupon analytics:', err);
      setError('Failed to fetch coupon analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate max usage for progress bars
  const maxUsage = data?.topCoupons?.length > 0
    ? Math.max(...data.topCoupons.map((c) => c.usageCount))
    : 0;

  if (authenticating) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: '20%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <Header isTabletOrMobile={isTabletOrMobile} />
      <Container maxWidth="xl">
        <Logo isTabletOrMobile={isTabletOrMobile} />

        <Box sx={{ mt: 2 }}>
          {/* Header */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              mb: 2,
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderLeft: (theme) => `4px solid ${theme.palette.success.main}`,
                px: 3,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <IconButton onClick={() => navigate('/olo-core-lookup')} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Box
                sx={{
                  backgroundColor: (theme) => alpha(theme.palette.success.main, 0.1),
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LocalOfferIcon sx={{ fontSize: 32, color: 'success.main' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Lato', color: 'text.primary' }}>
                  Coupon Analytics
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Analyze coupon usage across OLO Core orders
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Filters */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              mb: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <ToggleButtonGroup
                value={channel}
                exclusive
                onChange={(e, newChannel) => {
                  if (newChannel !== null) {
                    setChannel(newChannel);
                    setSelectedStore('all');
                  }
                }}
                size="small"
              >
                <ToggleButton value="core" sx={{ px: 2 }}>
                  <RestaurantIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  Core
                </ToggleButton>
                <ToggleButton value="catering" sx={{ px: 2 }}>
                  <GroupsIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  Catering
                </ToggleButton>
              </ToggleButtonGroup>

              <DatePicker
                selected={startDate}
                onChange={setStartDate}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                placeholderText="Start Date"
                customInput={
                  <TextField size="small" label="Start Date" sx={{ width: 150 }} autoComplete="off" />
                }
              />
              <DatePicker
                selected={endDate}
                onChange={setEndDate}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                placeholderText="End Date"
                customInput={
                  <TextField size="small" label="End Date" sx={{ width: 150 }} autoComplete="off" />
                }
              />

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Store</InputLabel>
                <Select
                  value={selectedStore}
                  onChange={(e) => setSelectedStore(e.target.value)}
                  label="Store"
                >
                  <MenuItem value="all">All Stores</MenuItem>
                  {stores.map((store) => (
                    <MenuItem key={store.storeNumber} value={store.storeNumber}>
                      {store.storeNumber} - {store.locationName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Tooltip title="Refresh data">
                <IconButton onClick={fetchData} size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : data ? (
            <>
              <Grid container spacing={2}>
                {/* Top Coupons by Description */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, height: '100%' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Top Coupons by Usage
                    </Typography>
                    <TableContainer sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>Description</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#fafafa', width: 80 }}>Codes</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#fafafa', width: 80 }}>Uses</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#fafafa', width: 100 }}>Discount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.topCoupons.map((coupon, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {coupon.couponDescription || 'No Description'}
                                  </Typography>
                                  <LinearProgress
                                    variant="determinate"
                                    value={(coupon.usageCount / maxUsage) * 100}
                                    sx={{ mt: 1, height: 4, borderRadius: 2 }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color="text.secondary">
                                  {formatNumber(coupon.uniqueCodes)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {formatNumber(coupon.usageCount)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color="warning.main">
                                  {formatCurrency(coupon.totalDiscount)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                          {data.topCoupons.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                                <Typography color="text.secondary">No coupon data available</Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>

                {/* Usage by Store */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, height: '100%' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Usage by Store
                    </Typography>
                    <TableContainer sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>Store</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#fafafa', width: 80 }}>Orders</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#fafafa', width: 80 }}>Coupons</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#fafafa', width: 100 }}>Discount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.usageByStore.map((store, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <StorefrontIcon fontSize="small" color="action" />
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {store.locationName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      #{store.storeNumber}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell align="right">{formatNumber(store.couponOrders)}</TableCell>
                              <TableCell align="right">{formatNumber(store.uniqueCoupons)}</TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color="warning.main">
                                  {formatCurrency(store.totalDiscount)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                          {data.usageByStore.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                                <Typography color="text.secondary">No store data available</Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>

                {/* Recent Orders */}
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Recent Coupon Orders
                    </Typography>
                    <TableContainer sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>Order</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>Coupon</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>Store</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>Time</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>Discount</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.recentOrders.map((order, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    color: channel === 'core' ? 'primary.main' : 'text.primary',
                                    cursor: channel === 'core' ? 'pointer' : 'default',
                                  }}
                                  onClick={() => {
                                    if (channel === 'core') {
                                      navigate(`/olo-core-lookup?search=${order.orderRef}`);
                                    } else {
                                      navigate(`/olo-lookup?search=${order.orderRef}`);
                                    }
                                  }}
                                >
                                  {order.orderRef}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={order.couponCode}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                                {order.couponDescription && (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {order.couponDescription}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{order.locationName}</Typography>
                                <Typography variant="caption" color="text.secondary">#{order.storeNumber}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{order.timePlaced}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500 }}>
                                  {formatCurrency(order.discount)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {formatCurrency(order.total)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                          {data.recentOrders.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                <Typography color="text.secondary">No recent coupon orders</Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>
            </>
          ) : null}
        </Box>
      </Container>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default CouponDashboard;
